import type { ReactNode } from 'react';
import { CardStatus, KycStatus, TxStatus } from '@shared/types';
import { WithdrawalStatus } from '@shared/ledger';
import { cn } from '@/lib/cn';

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent';

/**
 * Status is the one place saturated colour is allowed, so the badge is
 * deliberately small and low-chroma: a tinted plate with a matching hairline,
 * never a solid block. Colour is never the only signal — the label is always
 * spelled out.
 */
const TONES: Record<Tone, string> = {
  neutral: 'bg-sunken text-fg-secondary border-line',
  ok: 'bg-ok-50 text-ok-700 border-ok-500',
  warn: 'bg-warn-50 text-warn-700 border-warn-500',
  danger: 'bg-danger-50 text-danger-700 border-danger-500',
  info: 'bg-info-50 text-info-500 border-info-500',
  accent: 'bg-accent-50 text-fg-accent border-accent-500',
};

export interface BadgeProps {
  children: ReactNode;
  tone?: Tone;
  className?: string;
}

export function Badge({ children, tone = 'neutral', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border px-1.5 py-px',
        'text-2xs font-semibold uppercase tracking-wide leading-snug',
        // Russian status words run longer than the English ones they replaced;
        // a badge must never break across two lines inside a table cell.
        'whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const TX_TONES: Record<TxStatus, Tone> = {
  [TxStatus.Pending]: 'warn',
  [TxStatus.Confirmed]: 'accent',
  [TxStatus.Settled]: 'ok',
  [TxStatus.Expired]: 'neutral',
  [TxStatus.Disputed]: 'danger',
  [TxStatus.Cancelled]: 'neutral',
};

/** Display labels only — the wire values in `TxStatus` are never translated. */
const TX_LABELS: Record<TxStatus, string> = {
  [TxStatus.Pending]: 'Ожидает',
  [TxStatus.Confirmed]: 'Подтверждён',
  [TxStatus.Settled]: 'Выплачено',
  [TxStatus.Expired]: 'Истёк',
  [TxStatus.Disputed]: 'Спор',
  [TxStatus.Cancelled]: 'Отменён',
};

export function TxStatusBadge({ status }: { status: TxStatus }) {
  return <Badge tone={TX_TONES[status]}>{TX_LABELS[status]}</Badge>;
}

const CARD_TONES: Record<CardStatus, Tone> = {
  [CardStatus.Pending]: 'warn',
  [CardStatus.Verified]: 'ok',
  [CardStatus.Rejected]: 'danger',
};

const CARD_LABELS: Record<CardStatus, string> = {
  [CardStatus.Pending]: 'На проверке',
  [CardStatus.Verified]: 'Проверена',
  [CardStatus.Rejected]: 'Отклонена',
};

export function CardStatusBadge({ status }: { status: CardStatus }) {
  return <Badge tone={CARD_TONES[status]}>{CARD_LABELS[status]}</Badge>;
}

const KYC_TONES: Record<KycStatus, Tone> = {
  [KycStatus.NotStarted]: 'neutral',
  [KycStatus.Pending]: 'warn',
  [KycStatus.Approved]: 'ok',
  [KycStatus.Rejected]: 'danger',
};

const KYC_LABELS: Record<KycStatus, string> = {
  [KycStatus.NotStarted]: 'Не начата',
  [KycStatus.Pending]: 'На проверке',
  [KycStatus.Approved]: 'Одобрена',
  [KycStatus.Rejected]: 'Отклонена',
};

export function KycStatusBadge({ status }: { status: KycStatus }) {
  return <Badge tone={KYC_TONES[status]}>{KYC_LABELS[status]}</Badge>;
}

const WITHDRAWAL_TONES: Record<WithdrawalStatus, Tone> = {
  [WithdrawalStatus.Pending]: 'warn',
  [WithdrawalStatus.Approved]: 'accent',
  [WithdrawalStatus.Sent]: 'ok',
  [WithdrawalStatus.Rejected]: 'danger',
  [WithdrawalStatus.Failed]: 'danger',
};

/**
 * «Отправлен» is the only status that means the USDT has actually left, so it
 * is the only one wearing the settled tone. «Одобрен» is deliberately not
 * green: an approved withdrawal is still money the trader does not have.
 */
const WITHDRAWAL_LABELS: Record<WithdrawalStatus, string> = {
  [WithdrawalStatus.Pending]: 'На рассмотрении',
  [WithdrawalStatus.Approved]: 'Одобрен',
  [WithdrawalStatus.Sent]: 'Отправлен',
  [WithdrawalStatus.Rejected]: 'Отклонён',
  [WithdrawalStatus.Failed]: 'Не прошёл',
};

export function WithdrawalStatusBadge({ status }: { status: WithdrawalStatus }) {
  return <Badge tone={WITHDRAWAL_TONES[status]}>{WITHDRAWAL_LABELS[status]}</Badge>;
}
